import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import UserAvatar from './UserAvatar';
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import {
	changeConversationTitle,
	addUsersToConversation,
	createVideoChatLink,
	deleteTeamConversation,
} from '../../../actions/teams';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { UserPlus, Users, X, Video, EllipsisVertical } from 'lucide-react';
import { channelMembershipText } from './channels';
import { IconButton } from '@/components/ui/custom/IconButton';
import {
	CommandItem,
	CommandList,
	Command,
	CommandGroup,
	CommandInput,
} from '@/components/ui/command';
import GroupDetailsDialog from './GroupDetailsDialog';
import TeamConversationAvatar from './TeamConversationAvatar';
import TeamInfoPage from './TeamInfoPage';
import { cn } from '@/lib/utils';
import { ChevronRight, Info } from 'lucide-react';
import {
	ActionSheet,
	BottomSheetTextButton,
	MobileBackButton,
	MobileHeader,
	MobileHeaderButton,
	useMobilePanel,
} from '@/components/mobile';
import {
	PHONE_DIALOG_CLASS,
	PHONE_DIALOG_HEADER_CLASS,
	PHONE_DIALOG_TITLE_CLASS,
} from './phoneDialog';
import { getOnlineStatus } from './onlineStatus';
import { closeRemovedThread } from './useTeamConversationActions';

const MessageNavbar = ({
	onSidebarToggle,
	isCommentsCollapsed,
	onCommentsCollapseChange,
	shouldShowCloseButton,
	handleIsAttachmentsModalOpen,
	isAttachmentsModalOpen,
	// Phones: a navigation bar with Back, the title (tap for Info) and an
	// Info button that pushes the conversation's details as a screen.
	isMobile = false,
	onMobileBack,
	// the details button, where focus returns when the sidebar closes
	infoToggleRef,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [newTitle, setNewTitle] = useState('');
	// removed unused isTitleLoaded state
	const [userAddMenuOpen, setUserAddMenuOpen] = useState(false);
	const [showCreateVideoChatModal, setShowCreateVideoChatModal] =
		useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [addedUsers, setAddedUsers] = useState([]);
	const [searchedUser, setSearchedUser] = useState('');
	const [isDeletingGroup, setIsDeletingGroup] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const infoPanel = useMobilePanel('teamInfo');
	const users = useSelector((state) => state.users);
	const auth = useSelector((state) => state.auth);
	const teams = useSelector((state) => state.teams);

	const { assignedTypeSelected } = useSelector((state) => state.webchat);

	const { _id: _business } = auth;
	const { _activeTeamConversation } = teams;

	const activeTeamConversation = teams.teamConversations.find(
		(conv) => conv._id === _activeTeamConversation
	);

	useEffect(() => {
		setNewTitle(activeTeamConversation?.title);
	}, [activeTeamConversation?.title]);

	if (!_activeTeamConversation)
		return (
			<p className="border-b-2 ml-4 mb-4 p-4 italic">
				No conversation found
			</p>
		);

	const isRunner = Boolean(activeTeamConversation?.isRunner);
	const isChannel = Boolean(activeTeamConversation?.channel);

	const numberOfUsers = activeTeamConversation
		? activeTeamConversation.numberOfUsers
		: 0;

	const directMessage = assignedTypeSelected === 'DIRECT_MESSAGES';

	const userFilteredFromConversation = activeTeamConversation?._users?.find(
		(u) => u !== auth.user._id
	);

	const filteredUsers = users.filter(
		(user) =>
			!activeTeamConversation?._users?.includes(user._id) &&
			user.active &&
			!addedUsers.some((addedUser) => addedUser._id === user._id)
	);

	// find the other user's details
	const userFromDirectMessage = users.find(
		(u) => u._id === userFilteredFromConversation
	);

	const handleClick = () => {
		setIsEditing(true);
	};

	const handleTitleChange = (e) => {
		setNewTitle(e.target.value);
	};

	const handleAddUsersMenuOpen = () => {
		setMenuOpen(false);
		setUserAddMenuOpen(true);
	};

	const handleAddUsersMenuClose = () => {
		setSearchedUser('');
		setAddedUsers([]);
	};

	const handleCancelTitleChange = () => {
		setIsEditing(false);
	};

	const handleNewTitle = async () => {
		if (newTitle === activeTeamConversation?.title) {
			setIsEditing(false);
			return;
		}
		setIsEditing(false);

		const updatedTeamConversation = {
			...activeTeamConversation,
			title: newTitle,
		};

		dispatch(
			changeConversationTitle({
				teamConversation: updatedTeamConversation,
				_business,
			})
		);
		// title update finished
	};

	const handleAddUsersToConversation = (user) => {
		if (activeTeamConversation?._users?.includes(user._id)) return;
		if (addedUsers.includes(user)) return;
		setAddedUsers([...addedUsers, user]);
		setSearchedUser('');
	};

	const handleRemoveUsersFromAddedUsersArray = (user) => {
		const updatedUsers = addedUsers.filter((u) => u._id !== user._id);
		setAddedUsers(updatedUsers);
	};

	const addUsersToCurrentConversation = async () => {
		// setLoading(true);
		await dispatch(
			addUsersToConversation({
				_teamConversation: activeTeamConversation._id,
				users: addedUsers.map((user) => user._id),
				_business: activeTeamConversation._business,
			})
		);
		handleAddUsersMenuClose();
		// setLoading(false);
	};

	const handleDeleteGroup = async () => {
		setIsDeletingGroup(true);

		const result = await dispatch(
			deleteTeamConversation({
				_teamConversation: activeTeamConversation._id,
			})
		);

		setIsDeletingGroup(false);

		if (result?.error) {
			toast.error('Could not delete group', {
				description:
					result.message || 'Please try again in a few moments.',
			});
			return false;
		}

		toast.success(`${activeTeamConversation.title} was deleted`);
		setMenuOpen(false);
		if (isMobile) {
			// Phones take the deleted thread and its Info screen off the stack.
			closeRemovedThread({
				navigate,
				onMobileBack,
				infoOpen: infoPanel.isOpen,
				listPath: '/groupchat/groups',
			});
		} else {
			navigate('/groupchat/groups');
		}

		return true;
	};

	const handleCloseVideoChatDialog = () => {
		setShowCreateVideoChatModal(false);
	};

	const handleCreateVideoChatLink = async () => {
		await dispatch(
			createVideoChatLink({
				_teamConversation: activeTeamConversation._id,
			})
		);

		setShowCreateVideoChatModal(false);
	};

	const renameTo = (title) => {
		setNewTitle(title);
		dispatch(
			changeConversationTitle({
				teamConversation: { ...activeTeamConversation, title },
				_business,
			})
		);
	};

	if (!activeTeamConversation) {
		return (
			<div className="flex items-center bg-background border-b w-full justify-between px-4 py-2">
				<p className="text-sm italic text-muted-foreground">
					Conversation unavailable. It may be loading or was not
					found.
				</p>
			</div>
		);
	}

	if (isMobile && !isRunner) {
		const listPath = `/groupchat/${directMessage ? 'dms' : 'groups'}`;
		const headerTitle = directMessage
			? userFromDirectMessage?.name || 'Direct message'
			: activeTeamConversation.title || 'Untitled';
		const memberCount =
			activeTeamConversation._users?.length || numberOfUsers || 0;
		const headerSubtitle = directMessage
			? getOnlineStatus(userFromDirectMessage)
			: `${isChannel ? 'Channel · ' : ''}${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
		const canVideo = directMessage ? Boolean(userFromDirectMessage?.active) : true;

		return (
			<>
				<MobileHeader
					className="bg-background/95"
					left={
						<MobileBackButton fallback={listPath} onClick={onMobileBack} />
					}
					title={
						// the title and the line under it are one 44px target for Info
						<button
							type="button"
							onClick={infoPanel.open}
							className="flex min-h-11 w-full flex-col items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							aria-label={`${headerTitle}${headerSubtitle ? `, ${headerSubtitle}` : ''}, show details`}
						>
							<span className="w-full truncate">{headerTitle}</span>
							{headerSubtitle && (
								<span
									className={cn(
										'w-full truncate text-xs font-normal leading-tight',
										directMessage && userFromDirectMessage?.isOnline
											? 'text-emerald-600 dark:text-emerald-400'
											: 'text-muted-foreground'
									)}
								>
									{headerSubtitle}
								</span>
							)}
						</button>
					}
					right={
						<>
							{canVideo && (
								<MobileHeaderButton
									aria-label="Start video chat"
									onClick={() => setShowCreateVideoChatModal(true)}
								>
									<Video />
								</MobileHeaderButton>
							)}
							<MobileHeaderButton
								aria-label={directMessage ? 'Message details' : 'Group details'}
								data-onboarding="teams-details"
								onClick={infoPanel.open}
							>
								<Info />
							</MobileHeaderButton>
						</>
					}
				/>
				<ActionSheet
					open={showCreateVideoChatModal}
					onOpenChange={setShowCreateVideoChatModal}
					title="Start a video chat?"
					description="This posts a link that teammates can join for a video call."
					items={[
						{
							label: 'Start video chat',
							icon: Video,
							onSelect: handleCreateVideoChatLink,
						},
					]}
				/>
				<TeamInfoPage
					open={infoPanel.isOpen}
					onClose={infoPanel.close}
					onMobileBack={onMobileBack}
					conversation={activeTeamConversation}
					directMessage={directMessage}
					userFromDirectMessage={userFromDirectMessage}
					onStartVideo={handleCreateVideoChatLink}
					onAddPeople={handleAddUsersMenuOpen}
					onRename={renameTo}
					onDelete={handleDeleteGroup}
					isDeletingGroup={isDeletingGroup}
					isAttachmentsModalOpen={isAttachmentsModalOpen}
					handleIsAttachmentsModalOpen={handleIsAttachmentsModalOpen}
				/>
				{!directMessage && (
					<Dialog
						open={userAddMenuOpen}
						onOpenChange={(open) => {
							setUserAddMenuOpen(open);
							if (!open) handleAddUsersMenuClose();
						}}
					>
						<DialogContent mobileVariant="fullscreen" hideClose="mobile" className={PHONE_DIALOG_CLASS}>
							<DialogHeader className={PHONE_DIALOG_HEADER_CLASS}>
								<BottomSheetTextButton
									className="justify-self-start"
									onClick={() => {
										setUserAddMenuOpen(false);
										handleAddUsersMenuClose();
									}}
								>
									{isChannel ? 'Done' : 'Cancel'}
								</BottomSheetTextButton>
								<DialogTitle className={PHONE_DIALOG_TITLE_CLASS}>
									{isChannel ? 'Members' : 'Add people'}
								</DialogTitle>
								{isChannel ? (
									<span aria-hidden />
								) : (
									<BottomSheetTextButton
										strong
										className="justify-self-end"
										disabled={addedUsers.length === 0}
										onClick={async () => {
											await addUsersToCurrentConversation();
											setUserAddMenuOpen(false);
										}}
									>
										Add
									</BottomSheetTextButton>
								)}
							</DialogHeader>
							<DialogDescription className="sr-only">
								{isChannel
									? `${numberOfUsers} members. ${channelMembershipText(activeTeamConversation)}`
									: `Add teammates to ${activeTeamConversation.title}`}
							</DialogDescription>
							{isChannel ? (
								<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,var(--safe-bottom))] pt-3">
									<p className="pb-3 text-sm text-muted-foreground">
										{channelMembershipText(activeTeamConversation)}
									</p>
									{activeTeamConversation._users?.map((id) => {
										const member = users.find((user) => user._id === id);
										if (!member) return null;
										return (
											<div key={id} className="flex min-h-12 items-center gap-3 border-b border-border/50 py-1.5">
												<UserAvatar
													user={member}
													showName={false}
													showNameBelow={false}
													avatarWidth="36px"
													avatarHeight="36px"
													avatarFontSize="13px"
												/>
												<span className="min-w-0 flex-1 truncate text-base">{member.name}</span>
											</div>
										);
									})}
								</div>
							) : (
								<Command className="flex min-h-0 flex-1 flex-col rounded-none bg-transparent">
									{addedUsers.length > 0 && (
										<div
											role="group"
											aria-label="People to add"
											className="flex shrink-0 gap-2 overflow-x-auto border-b border-border/60 px-4 py-2 scrollbar-none"
										>
											{addedUsers.map((user) => (
												<button
													type="button"
													key={user._id}
													onClick={() => handleRemoveUsersFromAddedUsersArray(user)}
													aria-label={`Remove ${user.name}`}
													className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-muted px-3 text-sm active:bg-muted/60"
												>
													{user.name}
													<X className="size-4 text-muted-foreground" aria-hidden="true" />
												</button>
											))}
										</div>
									)}
									<CommandInput
										value={searchedUser}
										onValueChange={setSearchedUser}
										placeholder="Search team members"
										enterKeyHint="search"
									/>
									<CommandList className="max-h-none min-h-0 flex-1 overscroll-contain pb-[max(1rem,var(--safe-bottom))] kb-open:pb-4">
										{filteredUsers.length > 0 ? (
											<CommandGroup heading="Available to add">
												{filteredUsers.map((user) => (
													<CommandItem
														key={user._id}
														value={user.name}
														onSelect={() => handleAddUsersToConversation(user)}
														className="min-h-12 gap-3 px-2"
													>
														<UserAvatar
															user={user}
															showName={false}
															showNameBelow={false}
															avatarWidth="36px"
															avatarHeight="36px"
															avatarFontSize="13px"
														/>
														<span className="min-w-0 flex-1 truncate text-base">{user.name}</span>
													</CommandItem>
												))}
											</CommandGroup>
										) : (
											<p className="p-6 text-center text-sm text-muted-foreground">
												{addedUsers.length > 0
													? 'Everyone is selected'
													: 'No more team members to add'}
											</p>
										)}
									</CommandList>
								</Command>
							)}
						</DialogContent>
					</Dialog>
				)}
			</>
		);
	}

	return (
		<div className="flex min-h-[60px] w-full items-center justify-between gap-3 border-b border-border/60 bg-background px-4 py-2.5">
			{directMessage && !isRunner && (
				<div
					className="conversation-enter flex min-w-0 flex-1 items-center gap-3"
					key={activeTeamConversation?._id}
				>
					<TeamConversationAvatar
						directMessageUser={userFromDirectMessage}
						size={36}
					/>
					<div className="flex min-w-0 flex-col gap-0.5">
						<span className="truncate text-[15px] font-medium leading-5">
							{userFromDirectMessage?.name}
						</span>
						<span
							className={cn(
								'truncate text-xs leading-tight',
								userFromDirectMessage?.isOnline
									? 'text-emerald-600 dark:text-emerald-400'
									: 'text-muted-foreground'
							)}
						>
							{getOnlineStatus(userFromDirectMessage)}
						</span>
					</div>
				</div>
			)}
			{!directMessage && (
				<div
					className="conversation-enter flex min-w-0 flex-1 items-center"
					key={activeTeamConversation?._id}
				>
					<GroupDetailsDialog
						menuOpen={menuOpen}
						setMenuOpen={setMenuOpen}
						isEditing={isEditing}
						handleClick={handleClick}
						newTitle={newTitle}
						handleTitleChange={handleTitleChange}
						handleCancelTitleChange={handleCancelTitleChange}
						handleNewTitle={handleNewTitle}
						handleAddUsersMenuOpen={handleAddUsersMenuOpen}
						handleDeleteGroup={handleDeleteGroup}
						isDeletingGroup={isDeletingGroup}
						activeTeamConversation={activeTeamConversation}
					/>
				</div>
			)}
			<div className="flex shrink-0 items-center gap-0.5">
				<div>
					{!directMessage && !isRunner && (
						<Dialog
							open={userAddMenuOpen}
							onOpenChange={setUserAddMenuOpen}
						>
							<DialogTrigger>
								<IconButton
									toolTipTitle={isChannel ? 'Members' : 'Add members'}
									toolTipSide="bottom"
								>
									{isChannel ? (
										<Users className="size-4" />
									) : (
										<UserPlus className="size-4" />
									)}
								</IconButton>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle className="pr-0.5">
										{isChannel ? 'People in' : 'Add people to'}{' '}
										{activeTeamConversation?.title}{' '}
									</DialogTitle>
									<DialogDescription>
										{isChannel
											? `${numberOfUsers} members. ${channelMembershipText(activeTeamConversation)}`
											: `${numberOfUsers} members`}
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-2">
									<p className="font-semibold text-sm">
										Current members
									</p>
									<div className="max-h-48 border overflow-y-auto rounded-md">
										<div className="p-2 space-y-2">
											{activeTeamConversation?._users?.map(
												(user) => {
													const currentUser =
														users.find(
															(userToFind) =>
																userToFind._id ===
																user
														);
													return (
														<UserAvatar
															key={
																currentUser?._id
															}
															user={currentUser}
															showNameBelow={
																false
															}
															avatarWidth="30px"
															avatarHeight="30px"
															avatarFontSize="15px"
															nameFontSize="15px"
															fontWeightThin
														/>
													);
												}
											)}
										</div>
									</div>
								</div>
								{!isChannel && (<>
								<Command className="border rounded-md">
									<CommandInput
										autoFocus
										value={searchedUser}
										onValueChange={setSearchedUser}
										placeholder="Search team members..."
									/>
									<CommandList className="max-h-40">
										{filteredUsers.length > 0 ? (
											<CommandGroup heading="Available to add">
												{filteredUsers.map((user) => (
													<CommandItem
														key={user._id}
														value={user.name}
														onSelect={() =>
															handleAddUsersToConversation(
																user
															)
														}
													>
														{user.name}
													</CommandItem>
												))}
											</CommandGroup>
										) : (
											<p className="text-sm text-muted-foreground p-4 text-center">
												No more team members to add
											</p>
										)}
									</CommandList>
								</Command>

								{addedUsers.length > 0 && (
									<p className="font-semibold text-sm">
										Selected members
									</p>
								)}
								<div className="flex p-1 gap-1 items-center flex-wrap">
									{addedUsers.map((user) => (
										<CustomButton
											variant="outline"
											key={user._id}
											onClick={() =>
												handleRemoveUsersFromAddedUsersArray(
													user
												)
											}
										>
											{user.name}
											<X className="size-4" />
										</CustomButton>
									))}
								</div>
								</>)}
								<DialogFooter>
									<DialogClose asChild>
										<CustomButton
											variant="outline"
											onClick={handleAddUsersMenuClose}
										>
											Close
										</CustomButton>
									</DialogClose>
									{!isChannel && (
										<CustomButton
											disabled={addedUsers.length === 0}
											variant="default"
											onClick={addUsersToCurrentConversation}
										>
											Add
										</CustomButton>
									)}
								</DialogFooter>
							</DialogContent>
						</Dialog>
					)}
				</div>

				{!isRunner && (
					<Dialog
						onOpenChange={setShowCreateVideoChatModal}
						open={showCreateVideoChatModal}
					>
						<DialogTrigger>
							{!directMessage && (
								<IconButton
									aria-label="call"
									onClick={() =>
										setShowCreateVideoChatModal(true)
									}
									toolTipTitle="Start video chat"
									toolTipSide="bottom"
								>
									<Video className="size-4 text-primary" />
								</IconButton>
							)}

							{directMessage && userFromDirectMessage?.active && (
								<IconButton
									aria-label="call"
									onClick={() =>
										setShowCreateVideoChatModal(true)
									}
									toolTipTitle="Start video chat"
									toolTipSide="bottom"
								>
									<Video className="size-4 text-primary" />
								</IconButton>
							)}
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle id="alert-dialog-slide-title">
									Create video chat?
								</DialogTitle>
							</DialogHeader>
							{/* <DialogContentText
							id="alert-dialog-slide-description"
							color="textPrimary"
						>
							
						</DialogContentText> */}
							<p>
								This will create a link that teammates can join
								to have a video call.
							</p>
							<DialogFooter>
								<CustomButton
									onClick={handleCloseVideoChatDialog}
									variant="outline"
								>
									Cancel
								</CustomButton>
								<CustomButton
									onClick={handleCreateVideoChatLink}
									variant="default"
								>
									Start Video Chat
								</CustomButton>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				)}

				{!isRunner && (
					<IconButton
						ref={infoToggleRef}
						aria-label="details"
						data-onboarding="teams-details"
						onClick={() => onSidebarToggle()}
						toolTipSide="bottom"
						toolTipTitle={
							directMessage ? 'Message details' : 'Group details'
						}
					>
						<EllipsisVertical className="size-4" />
					</IconButton>
				)}

				{!isCommentsCollapsed && isRunner && shouldShowCloseButton && (
					<IconButton
						aria-label="collapse notes"
						onClick={onCommentsCollapseChange}
						toolTipTitle="Collapse notes"
						toolTipSide="bottom"
					>
						<ChevronRight className="size-4" />
					</IconButton>
				)}
			</div>
		</div>
	);
};

export default MessageNavbar;
